import { useState } from 'react'
import { Eye, Edit2, Trash2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { toDisplayDate } from '@/lib/utils'
import {
  useNoteSessionsByContact,
  useNoteSessionsByLead,
  useNoteSessionsByOpportunity,
  useNoteSessionsByAccount,
} from '@/hooks/use-api'
import type { Note } from '@/types'
import type { NoteSession } from '@/types/noteSession'

type MergedNoteRow =
  | {
      kind: 'quick'
      id: string
      title: string
      preview: string
      created_at: string
      owner_name: string | null
      raw: Note
    }
  | {
      kind: 'session'
      id: string
      title: string
      preview: string
      created_at: string
      owner_name: string | null
      raw: NoteSession
    }

interface MergedNotesListProps {
  entityType: 'Lead' | 'Contact' | 'Opportunity' | 'Account'
  entityId: string
  legacyNotes: Note[]
  onOpenSession: (sessionId: string) => void
  onOpenLegacyNote: (note: Note) => void
  onEditNote?: (note: Note) => void
  onDeleteNote?: (note: Note) => void
  onEditSession?: (session: NoteSession) => void
  onDeleteSession?: (session: NoteSession) => void
  currentUserId?: string
  currentUserType?: string
}

export function MergedNotesList({
  entityType,
  entityId,
  legacyNotes,
  onOpenSession,
  onOpenLegacyNote,
  onEditNote,
  onDeleteNote,
  onEditSession,
  onDeleteSession,
  currentUserId,
  currentUserType,
}: MergedNotesListProps) {
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null)
  // Pick the right hook based on entity type
  const contactSessionsQuery = useNoteSessionsByContact(entityType === 'Contact' ? entityId : '')
  const leadSessionsQuery = useNoteSessionsByLead(entityType === 'Lead' ? entityId : '')
  const opportunitySessionsQuery = useNoteSessionsByOpportunity(
    entityType === 'Opportunity' ? entityId : ''
  )
  const accountSessionsQuery = useNoteSessionsByAccount(entityType === 'Account' ? entityId : '')

  // Get the active query result
  const sessionsQuery =
    entityType === 'Contact'
      ? contactSessionsQuery
      : entityType === 'Lead'
        ? leadSessionsQuery
        : entityType === 'Account'
          ? accountSessionsQuery
          : opportunitySessionsQuery

  const sessions = sessionsQuery.data ?? []

  // Build merged list
  const mergedRows: MergedNoteRow[] = [
    // Legacy notes
    ...legacyNotes.map((note) => ({
      kind: 'quick' as const,
      id: note.id,
      title: note.title || '(Untitled)',
      preview: (note.body || '').slice(0, 200),
      created_at: note.created_date || '',
      owner_name: note.owner_name || null,
      raw: note,
    })),
    // Note sessions
    ...sessions.map((session) => ({
      kind: 'session' as const,
      id: session.id,
      title: session.title || '(Untitled session)',
      preview: (session.notes_plain_text || session.notes_content || '').slice(0, 200),
      created_at: session.started_at,
      owner_name: session.owner_name || null,
      raw: session,
    })),
  ]

  // Sort by created_at descending
  mergedRows.sort((a, b) => {
    const dateA = new Date(a.created_at).getTime()
    const dateB = new Date(b.created_at).getTime()
    return dateB - dateA
  })

  const formatDate = (date?: string) => {
    const d = toDisplayDate(date)
    if (!d) return 'N/A'
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const canEditNote = (row: MergedNoteRow) => {
    if (!currentUserId || !currentUserType) return false
    if (row.kind === 'session') {
      return row.raw.owner_id === currentUserId
    }
    return row.raw.owner_id === currentUserId || ['Admin', 'Super Admin'].includes(currentUserType)
  }

  if (mergedRows.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <p className="text-sm text-muted-foreground">
            No notes for this {entityType.toLowerCase()} yet.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      {mergedRows.map((row) => {
        const canAct = canEditNote(row)

        return (
          <Card key={row.id}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">{row.title}</CardTitle>
                  <Badge variant={row.kind === 'session' ? 'default' : 'secondary'}>
                    {row.kind === 'session' ? 'Call session' : 'Quick note'}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  {row.kind === 'quick' && row.raw.is_private && (
                    <Badge variant="secondary">Private</Badge>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {formatDate(row.created_at)}
                  </span>
                </div>
              </div>
              <CardDescription>By {row.owner_name || 'Unknown'}</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm whitespace-pre-wrap line-clamp-3">
                {row.preview || 'No content'}
              </p>
              <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (row.kind === 'session') {
                      onOpenSession(row.id)
                    } else {
                      onOpenLegacyNote(row.raw)
                    }
                  }}
                  aria-label={`View ${row.kind === 'session' ? 'session' : 'note'}: ${row.title}`}
                  className="focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Eye className="h-4 w-4 mr-2" aria-hidden="true" />
                  View
                </Button>

                {canAct && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (row.kind === 'session') {
                          onEditSession?.(row.raw)
                        } else {
                          onEditNote?.(row.raw)
                        }
                      }}
                      aria-label={`Edit ${row.kind === 'session' ? 'session' : 'note'}: ${row.title}`}
                      className="focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <Edit2 className="h-4 w-4 mr-2" aria-hidden="true" />
                      Edit
                    </Button>

                    <AlertDialog open={deletingNoteId === row.id}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeletingNoteId(row.id)}
                        aria-label={`Delete ${row.kind === 'session' ? 'session' : 'note'}: ${row.title}`}
                      >
                        <Trash2 className="h-4 w-4 mr-2" aria-hidden="true" />
                        Delete
                      </Button>

                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            Delete {row.kind === 'session' ? 'session' : 'note'}?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <div className="flex justify-end gap-2">
                          <AlertDialogCancel onClick={() => setDeletingNoteId(null)}>
                            Cancel
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => {
                              if (row.kind === 'session') {
                                onDeleteSession?.(row.raw)
                              } else {
                                onDeleteNote?.(row.raw)
                              }
                              setDeletingNoteId(null)
                            }}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </div>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )
      })}
    </>
  )
}
